import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import type { User } from '../../types';
import * as authService from '../../services/authService';
import { SpinnerIcon } from '../icons/SpinnerIcon';
import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';

export const UsersPanel: React.FC = () => {
    const { user } = useAuth();
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [lastUserDoc, setLastUserDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
    const [hasMoreUsers, setHasMoreUsers] = useState(true);

    const isAdmin = user && user.role === 'admin';

    const fetchUsers = useCallback(async () => {
        if (!isAdmin) return;
        setIsLoadingUsers(true);
        const { users, lastVisible } = await authService.getAllUsers();
        setAllUsers(users.filter(u => u.uid !== user?.uid)); // Exclude admin
        setLastUserDoc(lastVisible);
        setHasMoreUsers(!!lastVisible && users.length > 0);
        setIsLoadingUsers(false);
    }, [isAdmin, user?.uid]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const loadMoreUsers = async () => {
        if (!lastUserDoc || !isAdmin) return;
        setIsLoadingUsers(true);
        const { users, lastVisible } = await authService.getAllUsers(lastUserDoc);
        setAllUsers(prev => [...prev, ...users.filter(u => u.uid !== user?.uid)]);
        setLastUserDoc(lastVisible);
        setHasMoreUsers(!!lastVisible && users.length > 0);
        setIsLoadingUsers(false);
    };

    return (
        <div className="mt-8">
            <h2 className="text-xl font-bold text-indigo-700 mb-4">Admin: User List</h2>
            <div className="bg-indigo-50 rounded-lg p-6 border border-indigo-200 space-y-4">
                {allUsers.length > 0 && allUsers.map(u => (
                    <div key={u.uid} className="bg-white p-4 rounded-md border border-gray-200">
                        <p className="font-medium text-gray-800">{u.name} <span className="text-gray-500 font-normal">({u.email})</span></p>
                        <p className="text-sm text-gray-500">Status: {u.isPro ? `Pro (${u.plan})` : 'Free'}</p>
                    </div>
                ))}
                {isLoadingUsers && (<div className="flex justify-center items-center p-4"><SpinnerIcon /><span className="ml-2 text-indigo-700">Loading...</span></div>)}
                {hasMoreUsers && !isLoadingUsers && (
                    <div className="text-center mt-4">
                        <button onClick={loadMoreUsers} className="px-4 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-semibold rounded-lg shadow-sm transition-colors duration-300">Load More</button>
                    </div>
                )}
                {!hasMoreUsers && allUsers.length === 0 && !isLoadingUsers && (<p className="text-center text-gray-500">No other users found.</p>)}
            </div>
        </div>
    );
};